// @ts-nocheck

import { useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { createFlow } from '@/service/aiAgentApi';
import FlowChartDialog from './FlowChartDialog';

export default function FlowchartCard({
  buildPayload,
  agentName,
  onFlowGenerated,
  instructions = [],
  selectedKbId = '',
  completionRules = [],
}) {
  const [generating, setGenerating] = useState(false);
  const [flow, setFlow] = useState(null);
  const [showFlow, setShowFlow] = useState(false);


  const hasKb = Boolean(selectedKbId);
  const hasValidInstructions = instructions.some(
    (item) => item.enabled !== false && item.body?.trim(),
  );
  const hasCompletionRule = completionRules.some(
    (r) => r.type?.trim() && r.description?.trim(),
  );

  const missing = [];
  if (!hasKb) missing.push('a knowledge base');
  if (!hasValidInstructions) missing.push('an instruction');
  if (!hasCompletionRule) missing.push('a completion rule');
  const canGenerate = missing.length === 0;

  const missingLabel =
    missing.length > 1
      ? `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`
      : missing[0];

  const handleGenerate = async () => {
    if (!hasKb) {
      toast.error('Please select a knowledge base before generating the flow');
      return;
    }

    if (!hasValidInstructions) {
      toast.error('Please add at least one instruction before generating the flow');
      return;
    }

    if (!hasCompletionRule) {
      toast.error('Please add at least one completion rule (type and description) before generating the flow');
      return;
    }

    const payload = buildPayload ? buildPayload() : null;

    if (!payload) {
      toast.error('Nothing to generate a flow from yet.');
      return;
    }

    setGenerating(true);

    try {
      const result = await createFlow(payload, 'json');

      setFlow(result);
      setShowFlow(true);

      toast.success('Flow chart generated');

      onFlowGenerated?.(result);
    } catch (error) {
      toast.error(
        typeof error === 'string'
          ? error
          : 'Failed to generate flow chart',
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Card
        className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-500 overflow-hidden border-success/30 bg-gradient-to-b from-success/5 to-transparent"
      >
        <CardContent className="flex flex-col items-center gap-3 py-5 text-center">
          <p className="text-sm text-muted-foreground">
            Generate an interactive flowchart that visually maps your agent&apos;s conversation logic.
          </p>

          <Button
            variant="success"
            className="w-full"
            onClick={handleGenerate}
            disabled={generating || !canGenerate}
          >
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}

            {generating ? 'Generating…' : 'Generate flow chart'}
          </Button>

          {!canGenerate && (
            <p className="text-xs text-muted-foreground">
              Add {missingLabel} to enable the flow chart.
            </p>
          )}

          {flow && !generating && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setShowFlow(true)}
            >
              View last flow chart
            </Button>
          )}
        </CardContent>
      </Card>

      <FlowChartDialog
        open={showFlow}
        onOpenChange={setShowFlow}
        flow={flow}
        agentName={agentName}
      />
    </>
  );
}
